import { clsx } from "clsx";
import PropTypes from "prop-types";
import { Button, Col, Form } from "react-bootstrap";
import { form } from "../forms";
import { FieldWidget as Widget } from "../forms/components/Widget";
import { SubmitUrlModalDefaultMessages, UrlScraper } from "../lib/enums";
import RlcsConfigurationInput from "../modules/samples/components/reanalyze/rlcs-configuration-input/RlcsConfigurationInput";
import { Divider, RenderWhen, TooltipIcon } from "./index";
import Typography from "./typography/Typography";

const radios = [
  {
    value: UrlScraper.TI_CLOUD,
    label: "Spectra Intelligence Analysis",
    tooltipMsg:
      "Spectra Intelligence Analysis checks for related samples and URLs on Spectra Intelligence and performs Dynamic Analysis of the URL using RL Cloud Sandbox."
  },
  {
    value: UrlScraper.LOCAL,
    label: "Local Analysis",
    tooltipMsg:
      "Local analysis uses wget tool to crawl the URL and perform static analysis over the fetched content."
  }
];

const SubmitUrlForm = ({
  setShow,
  onSubmit,
  errors,
  values,
  onValueChange,
  isUrlDisabled,
  isTiCloudDisabled,
  isRLDynamicDisabled,
  isSubmitButtonDisabled,
  rlcsAnalysisInfo
}) => {
  const { scraper } = values || {};
  const isRadioDisabled = value =>
    value === UrlScraper.TI_CLOUD ? isTiCloudDisabled : false;
  const isLocalScraperSelected = values.scraper !== "cloud";

  return (
    <Form
      className="vertical-form"
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Widget
        name="url"
        type="text"
        placeholder="Enter URL"
        disabled={isUrlDisabled}
        label={
          <div className="flex mb-10">
            <Typography variant={"h5"}>URL</Typography>
            <TooltipIcon
              icon={"info-circle"}
              message="If no protocol is provided, HTTP will be used."
              className="url-upload-tooltip-icon"
            />
          </div>
        }
        vertical
        value={values.url}
        required
        errors={errors && errors.url}
        help={
          <span>
            RL Network Threat Intelligence and Spectra Analyze Networking
            Toolkit data will be collected by default.{" "}
            <a className="network-threat-intel_link" href="/config">
              Click here to change these settings.
            </a>
          </span>
        }
        data-testid="submit-url-input-field"
      />

      <Divider className={"my-30"} />

      <Form.Group className={"mb-30"}>
        {radios.map(radio => {
          const { value, label, tooltipMsg } = radio || {};
          return (
            <Form.Check
              id={`submit-url-form-radio-${value}`}
              data-testid={`submit-url-radio-${value}`}
              key={value}
              label={
                <div className="flex">
                  <Typography
                    variant={"subtitle2"}
                    className={clsx({ "text-bold": scraper === value })}
                  >
                    {label}
                  </Typography>
                  <TooltipIcon
                    icon="info-circle"
                    message={
                      value === UrlScraper.TI_CLOUD && isRadioDisabled(value)
                        ? SubmitUrlModalDefaultMessages.tiCloudDisabled
                        : tooltipMsg
                    }
                    className="url-upload-tooltip-icon"
                  />
                </div>
              }
              type="radio"
              name="scraper"
              inline
              onChange={() =>
                onValueChange({
                  scraper: value,
                  ...(value === UrlScraper.LOCAL && {
                    rl_dynamic_analysis: false
                  })
                })
              }
              disabled={isRadioDisabled(value)}
              checked={scraper === value}
            />
          );
        })}
      </Form.Group>

      <div className={"d-flex mb-10"}>
        <Typography variant={"h5"}>RL Analysis </Typography>
        <TooltipIcon
          icon="info-circle"
          message={
            "Chrome browser is used to open URLs. Using options below," +
            " fine tune the desired RL Cloud Sandbox detonation environment."
          }
          className="url-upload-tooltip-icon"
        />
      </div>
      <RlcsConfigurationInput
        rlcsSelected={values.rl_dynamic_analysis}
        rlcsParams={{
          platform: values.platform,
          locale: values.locale,
          geolocation: values.geolocation,
          interactive: values.interactive
        }}
        disabled={isLocalScraperSelected || isRLDynamicDisabled}
        analysisInfo={{
          enabled:
            window.tcbase.consts.rl_dynamic_analysis_enabled &&
            (rlcsAnalysisInfo.enabled === undefined ||
              rlcsAnalysisInfo.enabled),
          additional_information: rlcsAnalysisInfo.additional_information
        }}
        toggleRlcsSelected={() =>
          onValueChange({ rl_dynamic_analysis: !values.rl_dynamic_analysis })
        }
        toggleRlcsInteractiveSelected={updatedPlatform =>
          onValueChange({
            interactive: !values.interactive,
            platform: updatedPlatform
          })
        }
        onChangeRlcsPlatform={platform =>
          onValueChange({ platform: platform.value })
        }
        onChangeRlcsLocale={locale => onValueChange({ locale: locale.value })}
        onChangeRlcsGeolocation={geolocation =>
          onValueChange({ geolocation: geolocation.value })
        }
      />

      <Divider className={"my-30"} />

      <Form.Group>
        <Col>
          <div className="url-upload-buttons-container">
            <RenderWhen condition={scraper === "local"}>
              <span style={{ color: "red" }}>*</span>
              <Typography variant={"body1"} tag={"span"}>
                Spectra Analyze will access submitted URL.
              </Typography>
            </RenderWhen>
            <div className="url-upload-buttons-right">
              <Button
                onClick={setShow}
                data-testid="submit-url-cancel-button"
                variant="prompt-secondary"
                style={{ marginRight: "5px" }}
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                data-testid="submit-url-submit-button"
                variant="prompt-primary"
                disabled={isSubmitButtonDisabled}
              >
                SUBMIT
              </Button>
            </div>
          </div>
        </Col>
      </Form.Group>
    </Form>
  );
};

SubmitUrlForm.propTypes = {
  setShow: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  values: PropTypes.shape({
    url: PropTypes.string,
    scraper: PropTypes.string,
    platform: PropTypes.string,
    geolocation: PropTypes.string,
    locale: PropTypes.string,
    rl_dynamic_analysis: PropTypes.bool,
    interactive: PropTypes.bool
  }),
  onValueChange: PropTypes.func.isRequired,
  isUrlDisabled: PropTypes.bool,
  isTiCloudDisabled: PropTypes.bool,
  isRLDynamicDisabled: PropTypes.bool,
  isSubmitButtonDisabled: PropTypes.bool,
  errors: PropTypes.shape({
    url: PropTypes.arrayOf(PropTypes.string)
  }),
  rlcsAnalysisInfo: PropTypes.shape({
    enabled: PropTypes.bool,
    additional_information: PropTypes.string
  })
};

export default form(SubmitUrlForm);
